import type { PeerRecord, UserProfile } from '@skychat/shared-core';

type Translate = (key: string) => string;
type ReplacePeers = (updater: (current: PeerRecord[]) => PeerRecord[]) => void;

type FriendRequestDependencies = {
  t: Translate;
  selfId: string;
  previewMode: boolean;
  getPeers(): PeerRecord[];
  replacePeers: ReplacePeers;
  search(query: string): Promise<{ ok?: boolean; error?: unknown; results?: unknown }>;
  waitForSocketReady(): Promise<{ emit(event: string, payload: unknown): void }>;
  refreshPeers(): Promise<unknown>;
  delay?(milliseconds: number): Promise<void>;
};

function peerById(peers: PeerRecord[], id: string): PeerRecord | undefined {
  const normalizedId = id.toLowerCase();
  return peers.find((peer) => peer.type !== 'group' && peer.id.toLowerCase() === normalizedId);
}

function assertRequestablePeer(peer: PeerRecord | undefined, t: Translate): void {
  if (peer?.status === 'accepted') throw new Error(t('alreadyFriend'));
  if (peer?.status === 'incoming') throw new Error(t('incomingRequestPending'));
  if (peer?.status === 'pending' || peer?.status === 'outgoing') throw new Error(t('friendRequestPending'));
  if (peer?.status === 'rejected') throw new Error(t('requestRejectedAlready'));
}

export function selectFriendSearchResult(results: UserProfile[], query: string): UserProfile | undefined {
  const normalized = query.toLowerCase();
  return results.find((item) => String(item.id || '').toLowerCase() === normalized)
    || results.find((item) => String(item.nickname || '').toLowerCase() === normalized)
    || (results.length === 1 ? results[0] : undefined);
}

export async function sendMobileFriendRequest(toId: string, deps: FriendRequestDependencies): Promise<void> {
  const requestedId = String(toId || '').trim();
  if (!requestedId) throw new Error(deps.t('friendIdRequired'));
  if (deps.selfId && requestedId.toLowerCase() === deps.selfId.toLowerCase()) {
    throw new Error(deps.t('cannotAddSelf'));
  }
  assertRequestablePeer(peerById(deps.getPeers(), requestedId), deps.t);

  if (deps.previewMode) {
    deps.replacePeers((current) => current.some((peer) => peer.id.toLowerCase() === requestedId.toLowerCase())
      ? current
      : [{ id: requestedId, nickname: requestedId, status: 'outgoing', type: 'friend' } as PeerRecord, ...current]);
    return;
  }

  const searchResult = await deps.search(requestedId);
  if (searchResult.ok === false) throw new Error(String(searchResult.error || deps.t('failed')));
  const results = Array.isArray(searchResult.results) ? searchResult.results as UserProfile[] : [];
  const matchedUser = selectFriendSearchResult(results, requestedId);
  if (!matchedUser?.id) throw new Error(deps.t('friendNotFound'));

  const target = String(matchedUser.id).trim();
  assertRequestablePeer(peerById(deps.getPeers(), target), deps.t);
  const socket = await deps.waitForSocketReady();
  const pendingPeer = {
    ...matchedUser,
    id: target,
    nickname: matchedUser.nickname || target,
    status: 'pending',
    type: 'friend'
  } as PeerRecord;
  deps.replacePeers((current) => [
    pendingPeer,
    ...current.filter((peer) => peer.type === 'group' || peer.id.toLowerCase() !== target.toLowerCase())
  ]);

  socket.emit('friend:request', { toId: target });
  const delay = deps.delay || ((milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds)));
  let confirmed = false;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    await delay(attempt === 0 ? 260 : 320);
    await deps.refreshPeers().catch(() => undefined);
    const status = String(peerById(deps.getPeers(), target)?.status || '').toLowerCase();
    if (status === 'pending' || status === 'incoming' || status === 'accepted') {
      confirmed = true;
      break;
    }
    if (status === 'rejected') break;
  }
  if (confirmed) return;

  deps.replacePeers((current) => current.filter((peer) => {
    if (peer.type === 'group' || peer.id.toLowerCase() !== target.toLowerCase()) return true;
    return peer.status !== 'pending' && peer.status !== 'outgoing';
  }));
  throw new Error(deps.t('friendRequestFailed'));
}
